import { Button } from '@/ui/rapid-ui'
import type { StoreWhatsAppTemplateDataCategoryEnumKey } from '@jobilla/nexus-sdk'
import { AlertTriangleIcon, InfoIcon, XIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const SUPPORTED_LANGUAGES = [
	{ code: 'en_US', label: 'English (US)' },
	{ code: 'en_GB', label: 'English (UK)' },
	{ code: 'fi', label: 'Finnish' },
	{ code: 'sv', label: 'Swedish' },
	{ code: 'de', label: 'German' },
	{ code: 'fr', label: 'French' },
	{ code: 'es', label: 'Spanish' },
	{ code: 'nl', label: 'Dutch' },
	{ code: 'pl', label: 'Polish' },
]

const CATEGORIES = [
	{ value: 'UTILITY', label: 'Utility', description: 'Transactional messages (confirmations, updates)' },
	{ value: 'MARKETING', label: 'Marketing', description: 'Promotional messages (offers, announcements)' },
]

// Meta's cap for a template BODY component.
export const BODY_MAX_LENGTH = 1024

// Code points, so an emoji counts once — same as Nexus's mb_strlen.
function countCharacters(text: string): number {
	return Array.from(text).length
}

export interface TemplateFormData {
	name: string
	language: string
	category: StoreWhatsAppTemplateDataCategoryEnumKey
	bodyText: string
	exampleParameters: string[]
}

interface TemplateCreatorProps {
	onSubmit: (data: TemplateFormData) => Promise<void>
	onCancel: () => void
	isSubmitting: boolean
	mode?: 'create' | 'edit'
	initialValues?: TemplateFormData
}

export function TemplateCreator(
	{ onSubmit, onCancel, isSubmitting, mode = 'create', initialValues }: TemplateCreatorProps,
) {
	const { t } = useTranslation('common')
	// Name and language are immutable once a template exists; in edit mode the
	// identity fields are locked and only the body + examples can change.
	const isEdit = mode === 'edit'
	const [name, setName] = useState(initialValues?.name ?? '')
	const [language, setLanguage] = useState(initialValues?.language ?? 'en_US')
	const [category, setCategory] = useState<StoreWhatsAppTemplateDataCategoryEnumKey>(
		initialValues?.category ?? 'UTILITY',
	)
	const [bodyText, setBodyText] = useState(initialValues?.bodyText ?? '')
	const [exampleParams, setExampleParams] = useState<string[]>(initialValues?.exampleParameters ?? [])

	const { parameterCount, parameterError } = useMemo(() => {
		const matches = bodyText.match(/\{\{(\d+)}}/g)
		if (!matches) { return { parameterCount: 0, parameterError: null } }

		const numbers = [...new Set(matches.map((m) => parseInt(m.replace(/[{}]/g, ''), 10)))].sort((a, b) => a - b)

		if (numbers[0] !== 1) {
			return {
				parameterCount: numbers.length,
				parameterError: t('whatsappSettings.templateCreator.paramsMustStartFromOne'),
			}
		}

		for (let i = 0; i < numbers.length; i++) {
			if (numbers[i] !== i + 1) {
				return {
					parameterCount: numbers.length,
					parameterError: t('whatsappSettings.templateCreator.paramsMustBeSequential', { number: i + 1 }),
				}
			}
		}

		return { parameterCount: numbers.length, parameterError: null }
	}, [bodyText, t])

	const bodyWarnings = useMemo(() => {
		const warnings: string[] = []
		if (!bodyText) { return warnings }

		// URL shortener check (advisory for all languages)
		if (/bit\.ly|tinyurl|goo\.gl|t\.co|shorturl|ow\.ly/i.test(bodyText)) {
			warnings.push('URL shorteners are not allowed by Meta and will cause rejection. Use full URLs instead.')
		}

		// Category mismatch heuristic — keyword matching only works reliably in English
		if (language.startsWith('en')) {
			if (
				category === 'UTILITY'
				&& /\b(sale|discount|offer|promo|deal|free|limited time|act now|buy|shop)\b/i.test(bodyText)
			) {
				warnings.push(
					'This message sounds promotional. Consider using the "Marketing" category to avoid rejection.',
				)
			}

			if (
				category === 'MARKETING'
				&& !/\b(sale|discount|offer|promo|deal|free|new|launch|announce|special|exclusive)\b/i.test(bodyText)
				&& bodyText.length > 20
			) {
				warnings.push(
					'This message sounds transactional. The "Utility" category may be more appropriate and has faster approval.',
				)
			}
		}

		return warnings
	}, [bodyText, category, language])

	const handleBodyTextChange = useCallback((value: string) => {
		setBodyText(value)
		const matches = value.match(/\{\{(\d+)}}/g)
		const count = matches ? new Set(matches.map((m) => m.replace(/[{}]/g, ''))).size : 0
		setExampleParams((prev) => {
			if (prev.length === count) { return prev }
			if (prev.length < count) { return [...prev, ...Array(count - prev.length).fill('')] }
			return prev.slice(0, count)
		})
	}, [])

	const nameError = useMemo(() => {
		if (!name) { return null }
		if (!/^[a-z]/.test(name)) { return t('whatsappSettings.templateCreator.mustStartLowercase') }
		if (!/^[a-z][a-z0-9_]*$/.test(name)) { return t('whatsappSettings.templateCreator.onlyLowercaseAllowed') }
		return null
	}, [name, t])

	const previewText = useMemo(() => {
		let text = bodyText
		exampleParams.forEach((param, index) => {
			const placeholder = `{{${index + 1}}}`
			text = text.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), param || `[${index + 1}]`)
		})
		return text
	}, [bodyText, exampleParams])

	const bodyLength = countCharacters(bodyText)
	const bodyTooLong = bodyLength > BODY_MAX_LENGTH

	const canSubmit = name && bodyText && !nameError && !parameterError && !bodyTooLong
		&& (parameterCount === 0 || exampleParams.every((p) => p.trim()))

	const handleSubmit = useCallback(async () => {
		if (!canSubmit) { return }
		await onSubmit({ name, language, category, bodyText, exampleParameters: exampleParams })
	}, [canSubmit, name, language, category, bodyText, exampleParams, onSubmit])

	return (
		<motion.div
			initial={{ opacity: 0, y: -10 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: -10 }}
			className="border border-gray-200 rounded-xl bg-white overflow-hidden"
		>
			<div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
				<h3 className="font-medium text-gray-900">
					{isEdit
						? t('whatsappSettings.templateCreator.editTitle')
						: t('whatsappSettings.templateCreator.title')}
				</h3>
				<button type="button" onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600">
					<XIcon className="size-5" />
				</button>
			</div>

			<div className="p-4 space-y-4">
				<div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700">
					<InfoIcon className="size-4 mt-0.5 flex-shrink-0" />
					<div>
						<p>{t('whatsappSettings.templateCreator.approvalNotice')}</p>
						<p className="mt-1 text-xs text-blue-600">
							{t('whatsappSettings.templateCreator.approvalTimeNotice')}
						</p>
					</div>
				</div>

				<div>
					<label htmlFor="template-name" className="block text-sm font-medium text-gray-700 mb-1">
						{t('whatsappSettings.templateCreator.templateName')}
					</label>
					<input
						id="template-name"
						type="text"
						value={name}
						onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s/g, '_'))}
						placeholder="e.g., interview_invitation"
						disabled={isEdit}
						className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 disabled:bg-gray-50 disabled:text-gray-500 ${
							nameError ? 'border-red-300' : 'border-gray-200'
						}`}
					/>
					{nameError && <p className="mt-1 text-xs text-red-500">{nameError}</p>}
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div>
						<label htmlFor="template-language" className="block text-sm font-medium text-gray-700 mb-1">
							{t('whatsappSettings.templateCreator.language')}
						</label>
						<select
							id="template-language"
							value={language}
							onChange={(e) => setLanguage(e.target.value)}
							disabled={isEdit}
							className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 disabled:bg-gray-50 disabled:text-gray-500"
						>
							{SUPPORTED_LANGUAGES.map((lang) => (
								<option key={lang.code} value={lang.code}>{lang.label}</option>
							))}
						</select>
					</div>
					<div>
						<label htmlFor="template-category" className="block text-sm font-medium text-gray-700 mb-1">
							{t('whatsappSettings.templateCreator.category')}
						</label>
						<select
							id="template-category"
							value={category}
							onChange={(e) => setCategory(e.target.value as StoreWhatsAppTemplateDataCategoryEnumKey)}
							disabled={isEdit}
							className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 disabled:bg-gray-50 disabled:text-gray-500"
						>
							{CATEGORIES.map((cat) => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
						</select>
					</div>
				</div>

				{isEdit && (
					<p className="text-xs text-gray-400">
						{t('whatsappSettings.templateCreator.editIdentityLocked')}
					</p>
				)}

				<div>
					<label htmlFor="template-body" className="block text-sm font-medium text-gray-700 mb-1">
						{t('whatsappSettings.templateCreator.messageBody')}
					</label>
					<textarea
						id="template-body"
						value={bodyText}
						onChange={(e) => handleBodyTextChange(e.target.value)}
						placeholder="Hi {{1}}, thank you for applying for {{2}}!"
						rows={4}
						aria-invalid={bodyTooLong || undefined}
						aria-describedby={bodyTooLong
							? 'template-body-count template-body-error'
							: 'template-body-count'}
						className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none ${
							bodyTooLong ? 'border-red-300' : 'border-gray-200'
						}`}
					/>
					<div className="mt-1 flex items-start justify-between gap-3 text-xs">
						<p className="text-gray-400">
							{t('whatsappSettings.templateCreator.dynamicParamsHint')}
						</p>
						<p
							id="template-body-count"
							className={`flex-shrink-0 tabular-nums ${
								bodyTooLong ? 'text-red-500 font-medium' : 'text-gray-400'
							}`}
						>
							{t('whatsappSettings.templateCreator.bodyCharCount', {
								current: bodyLength,
								max: BODY_MAX_LENGTH,
							})}
						</p>
					</div>
					{bodyTooLong && (
						<p id="template-body-error" className="mt-1 text-xs text-red-500">
							{t('whatsappSettings.templateCreator.bodyTooLong', { max: BODY_MAX_LENGTH })}
						</p>
					)}
					{parameterError && <p className="mt-1 text-xs text-red-500">{parameterError}</p>}
					{bodyWarnings.length > 0 && (
						<div className="mt-2 space-y-1">
							{bodyWarnings.map((warning, index) => (
								<div key={index} className="flex items-start gap-1.5 text-xs text-amber-600">
									<AlertTriangleIcon className="size-3 mt-0.5 flex-shrink-0" />
									<span>{warning}</span>
								</div>
							))}
						</div>
					)}
				</div>

				{parameterCount > 0 && (
					<div className="space-y-2">
						<label className="block text-sm font-medium text-gray-700">
							{t('whatsappSettings.templateCreator.exampleValues')}
						</label>
						{Array.from({ length: parameterCount }, (_, i) => (
							<input
								key={i}
								type="text"
								value={exampleParams[i] ?? ''}
								onChange={(e) =>
									setExampleParams((prev) => {
										const next = [...prev]
										next[i] = e.target.value
										return next
									})}
								placeholder={`Example for {{${i + 1}}}`}
								className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
							/>
						))}
					</div>
				)}

				{bodyText && (
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-1">
							{t('whatsappSettings.templateCreator.preview')}
						</label>
						<div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
							{previewText}
						</div>
					</div>
				)}
			</div>

			<div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50">
				<Button type="subtle" size="short" onClick={onCancel}>{t('common.cancel')}</Button>
				<Button
					type="committal"
					size="short"
					onClick={() => void handleSubmit()}
					disabled={!canSubmit}
					isLoading={isSubmitting}
				>
					{isEdit
						? t('whatsappSettings.templateCreator.saveChanges')
						: t('whatsappSettings.templateCreator.createTemplate')}
				</Button>
			</div>
		</motion.div>
	)
}

export function TemplateStatusBadge({ status }: { status: string }) {
	const normalizedStatus = status.toUpperCase().replace(/_/g, ' ')
	const styles: Record<string, string> = {
		APPROVED: 'bg-green-100 text-green-700',
		PENDING: 'bg-amber-100 text-amber-700',
		REJECTED: 'bg-red-100 text-red-700',
		'NOT SUBMITTED': 'bg-gray-100 text-gray-600',
	}
	const displayText = normalizedStatus.charAt(0) + normalizedStatus.slice(1).toLowerCase()
	return (
		<span
			className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
				styles[normalizedStatus] ?? 'bg-gray-100 text-gray-600'
			}`}
		>
			{displayText}
		</span>
	)
}
